import { useCallback, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { TextField } from '@picsart/design-system/components/TextField';
import { IconCross } from '@picsart/design-system/foundation/icons/IconCross';
import { IconSearch } from '@picsart/design-system/foundation/icons/IconSearch';
import { useClickOutsideRef } from '@picsart/design-system/hooks/dom';
import { useTranslator } from '@picsart/rc/services/localization';

import useSearch from '@/hooks/useSearch';
import SearchBody from '@/components/SearchBody';
import SearchResults from '@/components/SearchResults';

import useStyles from './styles';

/**
 * Desktop/tablet search: a field that lives permanently in the header and
 * suggests results as you type (debounced), in a popover anchored under it.
 *
 * The mobile counterpart is the full-screen `SearchScreen`, which is opened
 * from an icon and tracked by `store.isSearchOpen`. This surface needs no such
 * flag — the field is always there; only the popover comes and goes.
 */
const SearchDropdown = () => {
  const classes = useStyles();
  const translate = useTranslator();
  // Dismissing hides the popover but keeps the term, so clicking away and
  // back doesn't cost the user what they typed.
  const [isDismissed, setIsDismissed] = useState(true);
  const {
    value,
    query,
    items,
    recentSearches,
    restrictionMessage,
    isFetching,
    isError,
    hasNoResults,
    handleChange: handleSearchChange,
    handleSubmit,
    handleClear,
    handleResultClick,
    handleRecentSelect,
    handleRecentRemove,
    handleRecentClearAll,
    handleRetry,
  } = useSearch();

  const dismiss = useCallback(() => setIsDismissed(true), []);

  // `false` = listen on mousedown, not click. On click, the press that focuses
  // the field would still be propagating when the listener registers and would
  // immediately count as an outside click.
  const containerRef = useClickOutsideRef(dismiss, [], false);

  const handleChange = (nextValue: string) => {
    setIsDismissed(false);
    handleSearchChange(nextValue);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') dismiss();
  };

  // Open on focus even with an empty field — that's where recent searches
  // live. `SearchBody` renders nothing when there's no query and no history,
  // so the popover stays collapsed for a first-time user.
  const hasPopoverContent = Boolean(query) || recentSearches.length > 0;

  return (
    <div ref={containerRef} className={classes.root}>
      <TextField
        // Not `type="search"` — WebKit adds its own clear button on top of
        // the DS one.
        type="text"
        className={classes.field}
        value={value}
        placeholder={translate('Search')}
        startIcon={IconSearch}
        // DS renders the end icon as a clear button, and only once filled.
        endIcon={IconCross}
        iconClick={handleClear}
        aria-label={translate('Search')}
        dataTestId="header-right-search"
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setIsDismissed(false)}
        // Live results already track typing; Enter just skips the debounce.
        onEnter={handleSubmit}
        onKeyDown={handleKeyDown}
      />
      {!isDismissed && hasPopoverContent && (
        <div className={classes.popover} data-testid="search-dropdown-popover">
          <SearchBody
            isCompact
            query={query}
            recentSearches={recentSearches}
            restrictionMessage={restrictionMessage}
            isFetching={isFetching}
            isError={isError}
            hasNoResults={hasNoResults}
            onRecentSelect={handleRecentSelect}
            onRecentRemove={handleRecentRemove}
            onRecentClearAll={handleRecentClearAll}
            onRetry={handleRetry}
          >
            <SearchResults
              items={items}
              isLoading={isFetching}
              onItemClick={handleResultClick}
            />
          </SearchBody>
        </div>
      )}
    </div>
  );
};

export default SearchDropdown;
